import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface TimelineItemProps {
  time: string;
  title: ReactNode;
  subtitle?: ReactNode;
  accent?: string;
  nextAccent?: string;
  isLast?: boolean;
  action?: ReactNode;
  to?: string;
  transition?: ReactNode;
  children?: ReactNode;
}

// meeting-detail-v7 timeline — compact native rows with colored dots, time rail, and inline actions.
export function TimelineItem({
  time,
  title,
  subtitle,
  accent = '#46a758',
  nextAccent,
  isLast = false,
  action,
  to,
  transition,
  children,
}: TimelineItemProps) {
  const lineEnd = nextAccent ?? accent;

  return (
    <li className="relative" role="listitem">
      <div className="flex gap-1.5 pb-3">
        <div className="relative flex w-2.5 shrink-0 flex-col items-center">
          {isLast ? null : (
            <span
              data-testid="timeline-line"
              className="absolute bottom-[-13px] left-1/2 top-[10px] w-[1.5px] -translate-x-1/2 rounded-full"
              style={{ background: `linear-gradient(${accent}33, ${lineEnd}22)` }}
              aria-hidden={transition ? undefined : true}
            >
              {transition ? (
                <span
                  data-testid="timeline-transition"
                  className="pointer-events-none absolute left-1/2 top-1/2 z-10 block w-max -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-surface px-1 text-[10px] font-medium leading-[12px] tracking-[-0.35px] text-ink-faint"
                >
                  {transition}
                </span>
              ) : null}
            </span>
          )}
          <span
            data-testid="timeline-dot"
            className="relative z-20 mt-1.5 h-[9px] w-[9px] rounded-full"
            style={{ backgroundColor: accent, color: accent }}
          >
            <span
              className="absolute inset-0 animate-ping rounded-full opacity-25"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            />
          </span>
        </div>
        <div className="w-10 shrink-0 pt-1 text-right text-[11px] font-medium leading-4 text-ink-faint">
          {time}
        </div>
        <div className="min-w-0 flex-1 pb-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {to ? (
                <Link
                  to={to}
                  className="block rounded-xl -m-1 p-1 focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  <div className="truncate text-[15px] font-semibold leading-5 tracking-[-0.25px] text-ink">
                    {title}
                  </div>
                  {subtitle ? (
                    <div className="mt-1 line-clamp-2 text-[13px] font-normal leading-4 text-ink-muted">
                      {subtitle}
                    </div>
                  ) : null}
                  {children ? <div className="mt-1.5">{children}</div> : null}
                </Link>
              ) : (
                <>
                  <div className="truncate text-[15px] font-semibold leading-5 tracking-[-0.25px] text-ink">
                    {title}
                  </div>
                  {subtitle ? (
                    <div className="mt-1 line-clamp-2 text-[13px] font-normal leading-4 text-ink-muted">
                      {subtitle}
                    </div>
                  ) : null}
                  {children ? <div className="mt-1.5">{children}</div> : null}
                </>
              )}
            </div>
            {action ? <div className="shrink-0">{action}</div> : null}
        </div>
        </div>
      </div>
      {isLast ? null : <div className="ml-[56px] h-px bg-surface-border/50" aria-hidden="true" />}
    </li>
  );
}
